import type { RefObject } from 'react';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { layoutGap, getModernScrollbar } from '../../../theme/theme';
import type { ValidationIssueFamilyCategory } from '../lib/validationIssueFamilies';
import type { ValidationBlockerFilter, ValidationSeverityFilter } from '../hooks/useValidationWorkspace';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import { WorkspacePaneHeader } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { WorkspaceFilterRail } from '../../../shared/ui/workflow/WorkspaceFilterRail';
import { SubsectionTitle } from '../../../shared/ui/typography';
import ValidationRailSearchSection from './ValidationRailSearchSection';
import ValidationRailFilterSections from './ValidationRailFilterSections';
import ValidationRailPassedSection from './ValidationRailPassedSection';

export interface ValidationGroupRailProps {
  severityFilter: ValidationSeverityFilter;
  onSelectSeverity: (filter: ValidationSeverityFilter) => void;
  blockerFilter: ValidationBlockerFilter;
  onSelectBlockerFilter: (filter: ValidationBlockerFilter) => void;
  requiredOnly: boolean;
  onToggleRequiredOnly: () => void;
  categoryFilter: ValidationIssueFamilyCategory | 'all';
  onSelectCategory: (filter: ValidationIssueFamilyCategory | 'all') => void;
  severityCounts: Record<ValidationSeverityFilter, number>;
  categoryCounts: Record<ValidationIssueFamilyCategory, number>;
  blockerCount: number;
  requiredOnlyCount: number;
  showPassedOnly: boolean;
  onShowPassedOnly: (value: boolean) => void;
  passedCollapsed: boolean;
  onTogglePassedCollapsed: () => void;
  passedCount: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  activeFilters: string[];
  onClearFilters: () => void;
  tourRefs?: {
    searchRef?: RefObject<HTMLDivElement | null>;
    filtersRef?: RefObject<HTMLDivElement | null>;
    repairAreaRef?: RefObject<HTMLDivElement | null>;
    passedRef?: RefObject<HTMLDivElement | null>;
  };
}

export default function ValidationGroupRail({
  severityFilter,
  onSelectSeverity,
  blockerFilter,
  onSelectBlockerFilter,
  requiredOnly,
  onToggleRequiredOnly,
  categoryFilter,
  onSelectCategory,
  severityCounts,
  categoryCounts,
  blockerCount,
  requiredOnlyCount,
  showPassedOnly,
  onShowPassedOnly,
  passedCollapsed,
  onTogglePassedCollapsed,
  passedCount,
  searchQuery,
  onSearchChange,
  activeFilters,
  onClearFilters,
  tourRefs,
}: ValidationGroupRailProps) {
  const theme = useTheme();
  const isCompactRail = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollbarStyles = getModernScrollbar(theme);

  return (
    <WorkspaceFilterRail sx={{ borderRight: isCompactRail ? 'none' : undefined }}>
      <WorkspacePaneHeader
        title={
          <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" flexWrap="wrap" useFlexGap>
      <SubsectionTitle>Triage filters</SubsectionTitle>
            <HelpTooltipTrigger
              label="Triage filters"
              help={validationActionTooltips.triageRail}
              placement="bottom-start"
            />
          </Stack>
        }
        description="Keep the issue queue focused."
        sx={{ px: isCompactRail ? 1.2 : 1.1, py: isCompactRail ? 1.05 : 1 }}
      />

      <Stack
        spacing={isCompactRail ? 0.6 : layoutGap.comfort}
        sx={{
          flex: 1,
          minHeight: 0,
          px: isCompactRail ? 1 : 0.85,
          py: isCompactRail ? 0.85 : layoutGap.comfort,
          overflowY: 'auto',
          ...scrollbarStyles,
        }}
      >
        <ValidationRailSearchSection
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          activeFilters={activeFilters}
          onClearFilters={onClearFilters}
          searchRef={tourRefs?.searchRef}
        />

        <ValidationRailFilterSections
          severityFilter={severityFilter}
          onSelectSeverity={onSelectSeverity}
          blockerFilter={blockerFilter}
          onSelectBlockerFilter={onSelectBlockerFilter}
          requiredOnly={requiredOnly}
          onToggleRequiredOnly={onToggleRequiredOnly}
          categoryFilter={categoryFilter}
          onSelectCategory={onSelectCategory}
          severityCounts={severityCounts}
          categoryCounts={categoryCounts}
          blockerCount={blockerCount}
          requiredOnlyCount={requiredOnlyCount}
          showPassedOnly={showPassedOnly}
          onShowPassedOnly={onShowPassedOnly}
          filtersRef={tourRefs?.filtersRef}
          repairAreaRef={tourRefs?.repairAreaRef}
        />

        <ValidationRailPassedSection
          showPassedOnly={showPassedOnly}
          onShowPassedOnly={onShowPassedOnly}
          passedCollapsed={passedCollapsed}
          onTogglePassedCollapsed={onTogglePassedCollapsed}
          passedCount={passedCount}
          onClearFilters={onClearFilters}
          passedRef={tourRefs?.passedRef}
        />
      </Stack>
    </WorkspaceFilterRail>
  );
}
